import { apiUrl } from "../utils/api";
import { invalidateCalendarMailbox } from "./calendarService";

/**
 * Lokaler Termin-Sidecar — KMVS-Zusatzdaten zu einem Graph-Event
 * (Notizen mit @Mention, Dokumente, Vorgang-Verknüpfung). Der Sidecar wird
 * server-seitig identifiziert über (mailbox, graphEventId) und bei der ersten
 * schreibenden Operation lazy angelegt.
 */

export interface CalendarTerminNoteDto {
    id: number;
    text: string;
    author: string;
    timestamp: string;
    mentions?: string[];
}

/**
 * Art-Kennzeichnung eines Termins. Frei wählbar sind „kundentermin",
 * „austausch" und — seit PO 21.09.2026 — „sonstige" (der Notartermin, die
 * Gerichtsverhandlung, der Gutachtertermin: weder Kundengespräch noch interne
 * Besprechung). „block" und „abwesenheit" vergibt das System auf eigenen Wegen.
 */
export type TerminArt = "kundentermin" | "austausch" | "sonstige" | "block" | "abwesenheit";

export type BlockTyp = "arbeit" | "mitteilung";
export type AbwesenheitTyp = "urlaub" | "krank" | "homeoffice";

/**
 * Ein dem Termin zugewiesener Kontakt (PO 21.09.2026). Spiegel von
 * `TerminKontaktDto` (Backend); `haupt` markiert den Hauptkontakt, der
 * zusätzlich in `kundeId` steht und in der Chip-Reihe vorn.
 */
export interface TerminKontaktDto {
    contactId: number;
    name: string;
    contactType: string | null;
    haupt: boolean;
}

export interface TerminMetaDto {
    /** null, solange noch kein Sidecar existiert (reiner Lese-Aufruf). */
    terminId: number | null;
    mailbox: string;
    graphEventId: string;
    vorgangId: number | null;
    /**
     * K9 — Titel des verknüpften Vorgangs (Kennzeichnung/Tooltip im Kalender).
     * null, wenn kein Vorgang hängt ODER der Betrachter ihn nicht lesen darf —
     * dann bleibt nur die `vorgangId` sichtbar.
     */
    vorgangTitel?: string | null;
    /**
     * Verknüpftes To-do (SP2). E06 (PO 22.08.2026) hatte die Verknüpfung aus
     * der Oberfläche genommen; seit PO 14.09.2026 wird sie wieder gesetzt —
     * eine Termin-Notiz hängt an EINEM To-do ihres Vorgangs.
     */
    todoId: number | null;
    /** Art-Kennzeichnung (null, solange unklassifiziert). */
    terminArt: TerminArt | null;
    /** Verknüpfter Kunde (nur bei terminArt='kundentermin'). */
    kundeId: number | null;
    /** Pflicht-Block-Typ (nur bei terminArt='block'). */
    blockTyp?: BlockTyp | null;
    /** Geschützter Pflicht-Block (Löschschutz). */
    pflichtBlock?: boolean;
    /** Abwesenheits-Typ (nur bei terminArt='abwesenheit'). */
    abwesenheitTyp?: AbwesenheitTyp | null;
    /**
     * Individuelle Termin-Farbe (SP6/A10, Entscheidung 65): Hex-Wert aus der
     * Preset-Palette (TERMIN_FARBEN in terminBlock.ts), KMVS-intern (kein
     * Outlook-Sync). null/fehlend = keine individuelle Farbe → Art-/Quellfarbe.
     */
    farbe?: string | null;
    /**
     * Nur von `getMeta`/`ensureMeta` (PO 28.09.2026): `kontakt`, wenn die
     * Nutzerin den Termin ausschließlich als FO/BO eines beteiligten Kontakts
     * sieht — Notizen dann schreibbar, Vorgang/Dokumente/Graph gesperrt.
     */
    zugang?: TerminZugangsWeg;
}

/**
 * Wie die Nutzerin einen Termin sieht: Kalenderrecht, FO/BO-Kontakt-Zugang
 * oder — seit PO 28.09.2026 — über den Vorgang, an dem der Termin hängt
 * (`vorgang`: nur lesen, auch die Notiz-Seite).
 */
export type TerminZugangsWeg = "kalender" | "kontakt" | "vorgang";

export interface LinkedTerminDto {
    terminId: number;
    mailbox: string;
    graphEventId: string;
    subject: string | null;
}

/**
 * Ein Kunden-Termin für die Mail-Kontakt-Sidebar (SP6, Reiter „Termine"):
 * Sidecar-Basis + best-effort angereicherte Graph-Anzeigedaten. Betreff/Zeit
 * bleiben null, wenn der Aufrufer aufs Postfach keinen Kalender-Lesezugriff hat.
 */
export interface KundeTerminDto {
    terminId: number;
    mailbox: string;
    graphEventId: string;
    subject: string | null;
    start: string | null;
    end: string | null;
    isAllDay: boolean;
    terminArt: TerminArt | null;
    farbe: string | null;
    /** Teams-Meeting? (PO 03.08.2026) — Badge + „Beitreten" in der Terminliste. */
    isOnlineMeeting?: boolean;
    /** Teams-Beitrittslink; null ohne Meeting/Kalenderzugriff (bei privaten
     *  Terminen fremder Postfächer serverseitig maskiert). */
    onlineMeetingUrl?: string | null;
}

/**
 * SP12 — Ein mit einem Vorgang (oder einem seiner Todos) verknüpfter Termin für
 * den Reiter „Termine" der Aktivitäts-Karte. Wie {@link KundeTerminDto}, plus die
 * Herkunft der Verknüpfung (direkt am Vorgang vs. über ein Todo des Vorgangs).
 */
export interface VorgangTerminDto extends KundeTerminDto {
    vorgangId: number | null;
    /** Über dieses To-do des Vorgangs verknüpft (seit PO 14.09.2026 wieder setzbar). */
    todoId: number | null;
    /**
     * Die Notiz-Seite des Termins (PO 14.09.2026: die Termin-Notiz erscheint
     * im Vorgang). null ohne Seite ODER ohne Kalender-Leserecht auf das
     * Postfach — das Backend liefert sie nur, wo `GET termin-notizseite`
     * auch antworten würde. `contentHtml` ist server-seitig sanitisiert, geht
     * vor dem Rendern trotzdem durch `reinigeBlockHtml`; `hatZeichnung` sagt,
     * dass die Seite einen Freihand-Layer trägt, den nur sie selbst zeigt.
     */
    notizSeite: VorgangTerminNotizSeiteDto | null;
    /**
     * T-220: In Outlook als privat/vertraulich markierter Termin aus dem
     * Kalender eines Kollegen. Er bleibt zu — `subject` ist der Platzhalter
     * „Privat", nur der Zeitraum ist zu sehen.
     */
    vertraulich?: boolean;
    /** Nur bei `vertraulich`: Es gibt eine (verborgene) Notiz-Seite mit Inhalt. */
    notizVorhanden?: boolean;
}

export interface VorgangTerminNotizSeiteDto {
    contentHtml: string | null;
    updatedAt: string | null;
    hatZeichnung: boolean;
}

/**
 * K1/J3 — ein mit EINER Mail verknüpfter Termin.
 *
 * `status` trennt drei Fälle, die sonst als leere Zeile ununterscheidbar wären:
 * `ok` (Graph-Daten geladen), `kein-zugriff` (Termin liegt in einem Kalender
 * ohne Leserecht — Zeile bleibt sichtbar, aber ohne Betreff) und `geloescht`
 * (der Termin wurde im Kalender gelöscht, der Sidecar steht noch → die Leiste
 * bietet „Verknüpfung entfernen" an).
 */
export interface MailTerminDto extends KundeTerminDto {
    linkId: number;
    vorgangId: number | null;
    vorgangTitel: string | null;
    status: "ok" | "kein-zugriff" | "geloescht";
}

/**
 * Snapshot der Mail, der beim Verknüpfen mitgeschrieben wird — 1:1 die Felder
 * des MailLink, damit die Termin-Ansicht ohne Graph-Roundtrip rendern kann.
 */
export interface MailTerminLinkInput {
    mailbox: string;
    messageId: string;
    conversationId?: string | null;
    /** RFC-Message-Id — entdoppelt zwei Kopien derselben Mail am selben Termin. */
    internetMessageId?: string | null;
    subject?: string | null;
    senderName?: string | null;
    senderEmail?: string | null;
    recipients?: string | null;
    receivedAt?: string | null;
    preview?: string | null;
}

/**
 * P9 — Ergebnis von „alle Mails des Vorgangs an diesen Termin hängen".
 * `vorhanden` sind die, die schon dort hingen (der Aufruf ist idempotent),
 * `ausgelassen` die wegen der Server-Obergrenze nicht übernommenen (ältesten).
 */
export interface VorgangsMailUebernahmeDto {
    terminId: number;
    angelegt: number;
    vorhanden: number;
    ausgelassen: number;
}

/** Eine mit einem Termin verknüpfte Mail (MailLink-Snapshot). */
export interface TerminMailDto {
    id: number;
    mailbox: string;
    messageId: string;
    conversationId?: string | null;
    subject?: string | null;
    senderName?: string | null;
    senderEmail?: string | null;
    recipients?: string | null;
    receivedAt?: string | null;
    preview?: string | null;
    linkedAt?: string | null;
}

/* ── Notiz-Seite im OneNote-Stil (Termine-SP7/A12) ───────────────────────── */

/**
 * Ein Freihand-Stroke des Zeichen-Layers (Zeichnen-MVP, Entscheidung 66):
 * Vektor-Punkte in Seiten-Koordinaten (CSS-Pixel) — dadurch bleibt jeder
 * Strich nachträglich einzeln löschbar (Radierer) und weiter editierbar.
 */
export interface NotizStrokeDto {
    id: string;
    /** Stiftfarbe (Hex, Preset NOTIZ_STIFT_FARBEN in notizSeite.ts). */
    color: string;
    /** Strichbreite in CSS-Pixeln. */
    width: number;
    points: [number, number][];
}

export interface NotizDrawingDto {
    strokes: NotizStrokeDto[];
}

export interface TerminNotizSeiteDto {
    terminId: number;
    mailbox: string;
    graphEventId: string;
    /** Freitext der Seite — server-seitig sanitisiertes HTML. */
    contentHtml: string | null;
    /** Zeichen-Layer; null = noch nichts gezeichnet. */
    drawing: NotizDrawingDto | null;
    updatedBy: string | null;
    updatedAt: string | null;
    /** Kalender-Schreibrecht aufs Sidecar-Postfach (FE-Gating; BE final). */
    canWrite: boolean;
    /** KMVS-Zuweisung erlaubt: api:write + Kalender-Leserecht. */
    canLink: boolean;
    /**
     * `kontakt` (PO 28.09.2026): nur über den FO/BO-Kontakt-Zugang sichtbar.
     * `canWrite` gilt dann ausschließlich für den Seiteninhalt — Dokumente,
     * Löschen und Bearbeiten des Termins bleiben gesperrt. Fehlt bei älteren
     * Servern → wie `kalender`.
     */
    zugang?: TerminZugangsWeg;
}

async function req<T>(url: string, options?: RequestInit): Promise<T> {
    const res = await fetch(url, {
        headers: { "Content-Type": "application/json" },
        ...options,
    });
    if (!res.ok) {
        const txt = await res.text();
        throw new Error(`API error ${res.status}: ${txt}`);
    }
    if (res.status === 204) return undefined as T;
    return res.json();
}

// mailbox (E-Mail) und graphEventId (langer Token) sind beide Pfad-Segmente
// und müssen kodiert werden.
const terminPath = (mailbox: string, eventId: string): string =>
    `calendar/termine/${encodeURIComponent(mailbox)}/${encodeURIComponent(eventId)}`;

export const calendarTerminService = {
    /** Sidecar-Meta NUR LESEN (terminId ist null, solange kein Sidecar existiert). */
    async getMeta(mailbox: string, eventId: string): Promise<TerminMetaDto> {
        return req<TerminMetaDto>(apiUrl(`${terminPath(mailbox, eventId)}/meta`));
    },

    /** Sidecar holen oder anlegen — liefert eine garantierte terminId (z. B. für Doc-Upload). */
    async ensureMeta(mailbox: string, eventId: string): Promise<TerminMetaDto> {
        return req<TerminMetaDto>(apiUrl(`${terminPath(mailbox, eventId)}/meta`), {
            method: "POST",
        });
    },

    /**
     * Sidecar-Meta vieler Events in EINEM Aufruf (Kalender-Ansicht, AP5).
     * Liefert nur Einträge für bereits klassifizierte Termine; unklassifizierte
     * Events fehlen in der Antwort. Vermeidet N+1 (ein Query je Postfach im
     * Backend). Postfächer ohne Kalender-Lesezugriff werden serverseitig still
     * übersprungen.
     */
    async metaBatch(
        items: { mailbox: string; graphEventId: string }[],
    ): Promise<TerminMetaDto[]> {
        if (items.length === 0) return [];
        return req<TerminMetaDto[]>(apiUrl("calendar/termine/meta/batch"), {
            method: "POST",
            body: JSON.stringify({ items }),
        });
    },

    /* ── Art-Kennzeichnung (Kundentermin/Austausch/Sonstige) ─────────────── */

    /**
     * Kennzeichnet einen Termin als 'kundentermin', 'austausch', 'sonstige'
     * oder 'abwesenheit' (TER-D2/TER-D3). Reine Kennzeichnung am Sidecar — der
     * Graph-Event-Titel bleibt original (das Abwesenheits-Keyword setzt der
     * Anlage-Pfad in den Titel). Bei 'kundentermin' optional eine kundeId
     * (Contact, tenant-geprüft); bei 'abwesenheit' der abwesenheitTyp und der
     * betroffene Mitarbeiter (ownerUserId).
     *
     * Bei 'sonstige' (PO 21.09.2026) wird `kundeId` NICHT mitgesendet: der
     * Server lässt den Kontakt dort stehen, und ein `null` von hier würde ihn
     * abräumen — wer einen zugewiesenen Termin auf „Sonstige" umstellt, verlöre
     * sonst seinen Hauptkontakt.
     */
    async setTerminArt(
        mailbox: string,
        eventId: string,
        input: {
            terminArt: "kundentermin" | "austausch" | "sonstige" | "abwesenheit";
            kundeId?: number | null;
            abwesenheitTyp?: AbwesenheitTyp | null;
            ownerUserId?: string | null;
        },
    ): Promise<TerminMetaDto> {
        const meta = await req<TerminMetaDto>(apiUrl("calendar/termin/art"), {
            method: "PATCH",
            body: JSON.stringify({
                mailbox,
                graphEventId: eventId,
                terminArt: input.terminArt,
                // 'sonstige' lässt den Kontakt in Ruhe (siehe Kommentar oben) —
                // das Feld wird gar nicht erst mitgeschickt.
                ...(input.terminArt === "sonstige"
                    ? {}
                    : {
                          kundeId:
                              input.terminArt === "kundentermin" ? input.kundeId ?? null : null,
                      }),
                abwesenheitTyp:
                    input.terminArt === "abwesenheit" ? input.abwesenheitTyp ?? null : null,
                ownerUserId:
                    input.terminArt === "abwesenheit" ? input.ownerUserId ?? null : null,
            }),
        });
        // Art/Klassifikation beeinflusst Grid-Farbe/Filter ⇒ Listen-Cache des
        // Postfachs verwerfen (SP4, leicht übersehbarer Schreibpfad).
        invalidateCalendarMailbox(mailbox);
        return meta;
    },

    /* ── Kontakt-Zuweisung des Termins (PO 21.09.2026) ───────────────────── */

    /**
     * Setzt die Kontakte eines Termins auf genau diese Menge — was nicht in der
     * Liste steht, wird entfernt. Die erste Id ist der Hauptkontakt (landet
     * zusätzlich in `kundeId`); eine leere Liste hebt die Zuweisung auf.
     *
     * Das Backend prüft Kalender-Schreibrecht (fremdes Postfach/Tenant ⇒
     * 403/404) und die Tenant-Zugehörigkeit jedes Kontakts.
     */
    async setTerminKontakte(
        mailbox: string,
        eventId: string,
        contactIds: number[],
        /** `artBeibehalten`: die Zuweisung lässt die Termin-Art stehen (das
         *  Termin-Fenster setzt sie selbst, PO 28.09.2026). */
        opts: { artBeibehalten?: boolean } = {},
    ): Promise<{ terminId: number; kontakte: TerminKontaktDto[] }> {
        const res = await req<{ terminId: number; kontakte: TerminKontaktDto[] }>(
            apiUrl("calendar/termin/kontakte"),
            {
                method: "PUT",
                body: JSON.stringify({
                    mailbox,
                    graphEventId: eventId,
                    contactIds,
                    ...(opts.artBeibehalten ? { artBeibehalten: true } : {}),
                }),
            },
        );
        // Die Zuweisung erzeugt/ändert den Sidecar und wandert in die
        // Termin-Art — der Listen-Cache des Postfachs ist damit veraltet.
        invalidateCalendarMailbox(mailbox);
        return res;
    },

    /** Zugewiesene Kontakte eines Termins (leer, solange kein Sidecar existiert). */
    async getTerminKontakte(
        mailbox: string,
        eventId: string,
    ): Promise<{ terminId: number | null; kontakte: TerminKontaktDto[] }> {
        return req<{ terminId: number | null; kontakte: TerminKontaktDto[] }>(
            apiUrl(`${terminPath(mailbox, eventId)}/kontakte`),
        );
    },

    /* ── Individuelle Termin-Farbe (SP6/A10) ─────────────────────────────── */

    /**
     * Setzt die individuelle Termin-Farbe am Sidecar (Preset-Hex aus
     * TERMIN_FARBEN) oder entfernt sie (null → Art-/Quellfarbe greift wieder).
     * KMVS-intern — der Graph-Event bleibt unangetastet; das Backend prüft
     * Kalender-Schreibrecht (fremdes Postfach/Tenant ⇒ 403/404) und validiert
     * die Palette (kein Freifeld).
     */
    async setTerminFarbe(
        mailbox: string,
        eventId: string,
        farbe: string | null,
    ): Promise<TerminMetaDto> {
        const meta = await req<TerminMetaDto>(apiUrl("calendar/termin/farbe"), {
            method: "PATCH",
            body: JSON.stringify({ mailbox, graphEventId: eventId, farbe }),
        });
        // Individuelle Termin-Farbe ⇒ Listen-Cache des Postfachs verwerfen (SP4).
        invalidateCalendarMailbox(mailbox);
        return meta;
    },

    /* ── Notizen ──────────────────────────────────────────────────────────── */

    async listNotes(mailbox: string, eventId: string): Promise<CalendarTerminNoteDto[]> {
        return req<CalendarTerminNoteDto[]>(apiUrl(`${terminPath(mailbox, eventId)}/notes`));
    },

    async createNote(
        mailbox: string,
        eventId: string,
        input: { text: string; mentions?: string[] },
    ): Promise<CalendarTerminNoteDto> {
        return req<CalendarTerminNoteDto>(apiUrl(`${terminPath(mailbox, eventId)}/notes`), {
            method: "POST",
            body: JSON.stringify(input),
        });
    },

    async updateNote(
        noteId: number,
        input: { text: string; mentions?: string[] },
    ): Promise<CalendarTerminNoteDto> {
        return req<CalendarTerminNoteDto>(apiUrl(`calendar/termin-notes/${noteId}`), {
            method: "PUT",
            body: JSON.stringify(input),
        });
    },

    async removeNote(noteId: number): Promise<void> {
        await req<void>(apiUrl(`calendar/termin-notes/${noteId}`), { method: "DELETE" });
    },

    /* ── Notiz-Seite im OneNote-Stil (Termine-SP7/A12) ───────────────────── */

    /**
     * Notiz-Seite eines Termins lesen (Route trägt die Sidecar-terminId).
     * Existiert noch keine Seite, kommen contentHtml/drawing als null zurück;
     * fremder Tenant/unbekannte Id ⇒ 404, fehlendes Kalender-Leserecht ⇒ 403.
     */
    async getNotizSeite(terminId: number): Promise<TerminNotizSeiteDto> {
        return req<TerminNotizSeiteDto>(apiUrl(`calendar/termin-notizseite/${terminId}`));
    },

    /**
     * Notiz-Seite speichern (Upsert). Partial-Semantik: nur übergebene Felder
     * werden angefasst — `undefined` lässt Text bzw. Zeichnung unverändert,
     * `null` leert das Feld. Das Backend sanitisiert autoritativ (HTML +
     * strikter Drawing-Neubau) und prüft das Kalender-Schreibrecht.
     */
    async saveNotizSeite(
        terminId: number,
        input: { contentHtml?: string | null; drawing?: NotizDrawingDto | null },
    ): Promise<TerminNotizSeiteDto> {
        return req<TerminNotizSeiteDto>(apiUrl(`calendar/termin-notizseite/${terminId}`), {
            method: "PUT",
            body: JSON.stringify(input),
        });
    },

    /* ── Vorgang-Verknüpfung ─────────────────────────────────────────────── */

    /** Termin mit einem Vorgang verknüpfen (vorgangId) oder lösen (null). */
    async setVorgang(
        mailbox: string,
        eventId: string,
        vorgangId: number | null,
    ): Promise<{ terminId: number; vorgangId: number | null }> {
        return req<{ terminId: number; vorgangId: number | null }>(
            apiUrl(`${terminPath(mailbox, eventId)}/vorgang`),
            { method: "PATCH", body: JSON.stringify({ vorgangId }) },
        );
    },

    /**
     * Termin mit einem To-do verknüpfen (todoId) oder lösen (null). SP2.
     * E06 (PO 22.08.2026) hatte das Setzen aus der Oberfläche genommen; seit
     * PO 14.09.2026 wird hier wieder gesetzt: die Termin-Notiz hängt an EINEM
     * To-do ihres Vorgangs. Das Backend prüft, dass To-do und Vorgang des
     * Termins zusammenpassen (400 sonst); der Aufrufer muss den Vorgang vorher
     * setzen.
     */
    async setTodo(
        mailbox: string,
        eventId: string,
        todoId: number | null,
    ): Promise<{ terminId: number; todoId: number | null }> {
        return req<{ terminId: number; todoId: number | null }>(
            apiUrl(`${terminPath(mailbox, eventId)}/todo`),
            { method: "PATCH", body: JSON.stringify({ todoId }) },
        );
    },

    /** Mit einem Vorgang verknüpfte Termine (für die Vorgang-Detailansicht). */
    async listForVorgang(vorgangId: number): Promise<LinkedTerminDto[]> {
        return req<LinkedTerminDto[]>(apiUrl(`vorgang/${vorgangId}/termine`));
    },

    /**
     * SP6 — Termine eines Kunden (Mail-Kontakt-Sidebar, Reiter „Termine").
     * Quelle sind die Sidecar-Zeilen mit passender kundeId (nur Kundentermine),
     * best-effort mit Graph-Betreff/-Zeit angereichert.
     */
    async listTermineByKunde(kundeId: number): Promise<KundeTerminDto[]> {
        return req<KundeTerminDto[]>(apiUrl(`calendar/termine/by-kunde/${kundeId}`));
    },

    /**
     * SP12 — Termine eines Vorgangs (inkl. der an seinen Todos hängenden Termine)
     * für den Reiter „Termine" der Aktivitäts-Karte.
     */
    async listTermineByVorgang(vorgangId: number): Promise<VorgangTerminDto[]> {
        return req<VorgangTerminDto[]>(apiUrl(`calendar/termine/by-vorgang/${vorgangId}`));
    },

    /**
     * Termine, die an EINEM To-do hängen — die rein lesende Terminliste der
     * aufgeklappten To-do-Zeile (`TodoTermineTab`, PO 30.08.2026). Seit
     * PO 14.09.2026 entstehen solche Verknüpfungen wieder (Termin-Notiz an ein
     * To-do des Vorgangs); E06 ist damit aufgehoben.
     */
    async listTermineByTodo(todoId: number): Promise<VorgangTerminDto[]> {
        return req<VorgangTerminDto[]>(apiUrl(`calendar/termine/by-todo/${todoId}`));
    },

    /* ── Mail ↔ Termin (K1) ──────────────────────────────────────────────── */

    /**
     * K1 — eine Mail an einen Termin hängen. Idempotent: derselbe Aufruf
     * zweimal legt kein Duplikat an (`created: false`). Serverseitig genügt
     * Kalender-LESErecht auf das Termin-Postfach (E10) — im Graph-Kalender
     * ändert sich nichts, geschrieben wird nur die KMVS-Verknüpfung.
     */
    async linkMailToTermin(
        mailbox: string,
        eventId: string,
        input: MailTerminLinkInput,
    ): Promise<{ linkId: number; terminId: number; created: boolean }> {
        return req<{ linkId: number; terminId: number; created: boolean }>(
            apiUrl(`${terminPath(mailbox, eventId)}/mails`),
            {
                method: "POST",
                body: JSON.stringify({
                    mailMailbox: input.mailbox,
                    messageId: input.messageId,
                    conversationId: input.conversationId ?? null,
                    internetMessageId: input.internetMessageId ?? null,
                    subject: input.subject ?? null,
                    senderName: input.senderName ?? null,
                    senderEmail: input.senderEmail ?? null,
                    recipients: input.recipients ?? null,
                    receivedAt: input.receivedAt ?? null,
                    preview: input.preview ?? null,
                }),
            },
        );
    },

    /**
     * K1 — die mit einem Termin verknüpften Mails (Snapshot, ohne Graph-Abruf).
     * Seit P10 je Mail sichtbarkeitsgefiltert: die Liste kann kürzer sein als
     * das, was tatsächlich am Termin hängt.
     */
    async listMailsForTermin(mailbox: string, eventId: string): Promise<TerminMailDto[]> {
        return req<TerminMailDto[]>(apiUrl(`${terminPath(mailbox, eventId)}/mails`));
    },

    /**
     * P9 — alle Mails EINES Vorgangs an den Termin hängen.
     *
     * Idempotent: ein zweiter Aufruf legt nichts an, sondern holt nur die
     * Mails nach, die seither an den Vorgang gekommen sind. Genau deshalb
     * bietet die Oberfläche ihn auch als eigene Aktion je Termin an und nicht
     * nur beim Verknüpfen.
     */
    async uebernimmVorgangsMails(
        mailbox: string,
        eventId: string,
        vorgangId: number,
    ): Promise<VorgangsMailUebernahmeDto> {
        return req<VorgangsMailUebernahmeDto>(
            apiUrl(`${terminPath(mailbox, eventId)}/mails/aus-vorgang`),
            { method: "POST", body: JSON.stringify({ vorgangId }) },
        );
    },

    /**
     * K1/J3 — die mit EINER Mail verknüpften Termine. Mit `conversationId`
     * threadweit, damit auch eine Antwort den Termin zeigt.
     */
    async listTermineByMail(
        mailbox: string,
        messageId: string,
        conversationId?: string | null,
    ): Promise<MailTerminDto[]> {
        const params = new URLSearchParams({ mailbox, messageId });
        if (conversationId) params.set("conversationId", conversationId);
        return req<MailTerminDto[]>(apiUrl(`calendar/termine/by-mail?${params.toString()}`));
    },

    /** K1 — eine Mail↔Termin-Verknüpfung lösen (der Termin selbst bleibt). */
    async unlinkMailFromTermin(linkId: number): Promise<void> {
        await req<{ ok: true }>(apiUrl(`calendar/termin-mails/${linkId}`), { method: "DELETE" });
    },
};
